import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Ticket } from "lucide-react";
import { api, fileUrl, showSlug } from "@/lib/api";
import Reveal from "@/components/Reveal";
import StickyBookButton from "@/components/StickyBookButton";

function fmt(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return { day: "--", month: "TBA", weekday: "" };
  return {
    day: d.toLocaleDateString("en-IN", { day: "2-digit" }),
    month: d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase(),
    weekday: d.toLocaleDateString("en-IN", { weekday: "long" }),
  };
}

export default function UpcomingShows() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/shows").then(({ data }) => setShows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="upcomingshows-page">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-bone/10 sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <Link to="/" className="flex items-baseline gap-2" data-testid="shows-logo">
          <span className="font-hindi text-2xl text-saffron">सुमिरन</span>
          <span className="font-display text-sm tracking-[0.25em] text-bone">THE BAND SUMIRAN</span>
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/bookprivateshow" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="shows-private-link">Book a Private Show</Link>
          <Link to="/" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="shows-back-home">Back to site</Link>
        </div>
      </header>

      <main className="px-6 md:px-12 py-14 md:py-20 max-w-6xl mx-auto">
        <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Live</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-bone leading-tight" data-testid="upcomingshows-heading">
          Upcoming <em className="text-gold">sandhyas</em>
        </h1>
        <p className="mt-4 text-ash text-sm max-w-lg">Where the band is performing next. Entry details are announced with each event.</p>

        {loading && <p className="py-16 text-ash" data-testid="shows-loading">Loading shows…</p>}
        {!loading && shows.length === 0 && (
          <p className="py-16 text-ash" data-testid="shows-empty">New dates announcing soon — follow @thebandsumiran on Instagram to hear first.</p>
        )}

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shows-grid">
          {shows.map((s, i) => {
            const d = fmt(s.date);
            const from = s.tickets?.length ? Math.min(...s.tickets.map((t) => t.price)) : s.price;
            return (
              <Reveal key={s.id} delay={i * 0.06} y={28}>
                <div className="group border border-bone/10 bg-surface/50 h-full flex flex-col hover:border-saffron/40 transition-colors" data-testid={`upcoming-show-${i}`}>
                  <div className="relative overflow-hidden aspect-[4/3]">
                    {s.image_url ? (
                      <img src={fileUrl(s.image_url)} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    ) : (
                      <div className="w-full h-full bg-surface flex items-center justify-center">
                        <span className="font-hindi text-4xl text-saffron/30">सुमिरन</span>
                      </div>
                    )}
                    <div className="absolute top-4 left-4 bg-ink/85 backdrop-blur border border-bone/15 px-3 py-2 text-center">
                      <p className="font-serif text-2xl text-bone leading-none">{d.day}</p>
                      <p className="font-display text-[10px] tracking-[0.25em] text-saffron mt-1">{d.month}</p>
                    </div>
                    {s.ticketed && (
                      <span className="absolute top-4 right-4 text-[9px] font-display tracking-[0.2em] text-ink bg-saffron px-2 py-1">TICKETED</span>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-serif text-2xl text-bone leading-tight">{s.name}</h3>
                    <p className="mt-2.5 flex items-center gap-2 text-sm text-ash">
                      <MapPin size={13} className="text-saffron shrink-0" />
                      {s.maps_url ? (
                        <a href={s.maps_url} target="_blank" rel="noreferrer" className="hover:text-saffron underline underline-offset-4 decoration-bone/20 transition-colors" data-testid={`upcoming-maps-${i}`}>{s.venue}</a>
                      ) : s.venue}
                    </p>
                    <p className="mt-1.5 flex items-center gap-2 text-sm text-ash"><Clock size={13} className="text-saffron shrink-0" /> {d.weekday} · {s.time}</p>
                    {s.description && <p className="mt-3 text-sm text-bone/60 line-clamp-2">{s.description}</p>}
                    <div className="mt-auto pt-5 flex items-end justify-between gap-3">
                      {s.ticketed ? (
                        <>
                          <div>
                            <p className="text-[10px] tracking-[0.2em] text-ash uppercase">Tickets from</p>
                            <p className="font-serif text-2xl text-gold">₹{from.toLocaleString("en-IN")}</p>
                          </div>
                          <Link to={`/book/${showSlug(s)}`}
                            className="flex items-center gap-2 px-5 py-3 bg-saffron text-ink text-xs font-bold tracking-[0.15em] hover:bg-gold transition-colors whitespace-nowrap"
                            data-testid={`upcoming-book-btn-${i}`}>
                            <Ticket size={15} /> BOOK NOW
                          </Link>
                        </>
                      ) : (
                        <p className="text-[10px] tracking-[0.18em] text-ash uppercase">Free / RSVP entry</p>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </main>
      <StickyBookButton onClick={() => document.querySelector('[data-testid="shows-grid"]')?.scrollIntoView({ behavior: "smooth" })} label="Book Now" />
    </div>
  );
}
